import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import Button from "../../components/buttons/Button";
import Image from "../../components/images/Image";
import Shimmer from "../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../services/useApi";
import useTranslation from "../../components/CustomHooks/useTranslation";
import config from "../../config/config";
import { useNavigate, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { CVM, MasterDistributor, NationalID, Passport, ServiceFee } from "../UserManagement/MasterDistributorManagement/Components/types";
import { formatDate } from "../../components/commonfunctions/convertDate";
import usePrivilege from "../../components/CustomHooks/usePrivilege";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ToolTip from "../../components/ToolTip/ToolTip";
import { setUserUrl } from "../../store/slices/geolocationSlice";
import { showToast } from "../../components/toastMessage/toastSlice";
import { getFirefoxCssFix } from "../../components/commonfunctions/getFirefoxCssFix";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import moment from "moment";
import CustomModal from "../../components/CustomModal";
import AddModel from "../../components/Add/AddModel";
import AddInputs from "../../components/Add/AddInputs";
import { setCompleteServiceFee } from "../UserManagement/MasterDistributorManagement/addMDSlice";
import UserRoleAndBusinessArea from './UserRoleAndBusinessArea';
import ServiceFeeAndRoleSelection, { ServiceFeeAndRoleSelectionRef } from './ServiceFeeAndRoleSelection';
import AddBottomButton from "../../components/Add/AddBottomButton";
import { resetBusinessAreas, setProvinces, setRegion } from "../../store/slices/businessAreasSlice";
import { ServiceFeeData } from "../MNOManagament/OnTargetAchievement/types";
import DropdownButtonForSearch from "../../components/Dropdowns/DropdownButtonForSearch";
import TextArea from "../../components/inputfields/TextArea";

interface UserManagementProps {
  type: string
  testing?: boolean;
  tab?: string;
}

const renderFields = (details: any, fields: { key: string; label: string }[]) => {
  return fields.map(({ key, label }) => {
    const isFileField =
      key === "passport_front_page" ||
      key === "national_id_front_page" ||
      key === "national_id_back_page";
    if (isFileField && details?.[key]) {
      const fileUrl = config?.cdnURL + "public/" + details?.[key];
      const fileName = details?.[key].split("/").pop();
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);

      return (
        <div key={key} className={`${key === "passport_front_page" ? '' : 'mt-2'}`}>
          <h1 className="text-sm text-gray-600">{label}</h1>

          <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 print:hidden">
            <p className="more-lines">{fileName}</p>
            <Image
              src="file-eye"
              className="cursor-pointer"
              objectFit="contain"
              onClick={() => window.open(fileUrl, "_blank")}
              testId={`${key.replace(/_/g, "-")}-image`}
            />
          </div>

          {/* Visible only in print mode */}
          <div className="hidden mt-2 print:block ">
            {isImage ? (
              <img
                src={fileUrl}
                alt={fileName}
                className="h-[200px] w-[200px] object-contain"
              />
            ) : (
              <p className="text-sm text-gray-600">Unsupported file type</p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div key={key} className="grid-item">
        <h1 className="text-sm text-gray-600">{label}</h1>
        {(label === 'Valid From' || label === 'Valid Up To')
          ? <div className="group relative">
            <p className="mt-1 font-medium text-gray-900" data-testid={key.replace(/_/g, '-')}>{formatDate(details?.[key])}</p>
            <ToolTip ToolTipItem={formatDate(details?.[key])} />
          </div>
          : <div className="group relative">
            <p className="mt-1 truncate font-medium text-gray-900 " data-testid={key.replace(/_/g, '-')}>{details?.[key] ? details?.[key] : key === '' ? '' : "-"}</p>
            {details?.[key] && <ToolTip ToolTipItem={details?.[key]} />}
          </div>
        }
      </div>
    );
  });
};


const passportFields = [
  { key: 'passport_number', label: 'Passport Number' },
  { key: 'passport_valid_from', label: 'Valid From' },
  { key: 'passport_valid_to', label: 'Valid Up To' },
  { key: 'passport_front_page', label: 'Passport Front Page' },
];

const nationalIdFields = [
  { key: 'national_id_number', label: 'National ID Number' },
  { key: 'national_id_valid_from', label: 'Valid From' },
  { key: 'national_id_valid_to', label: 'Valid Up To' },
  { key: 'mothers_name', label: `Mother's Name` },
  { key: '', label: '' },
  { key: 'national_id_front_page', label: 'National ID Front Page' },
  { key: 'national_id_back_page', label: 'National ID Back Page' },
];

export default function ViewOnBoardingRequests({ type, testing, tab }: UserManagementProps) {
  const { getAPI, PatchAPI } = useApi();
  const { translate, notEnglish } = useTranslation(adminContents);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const hasEditMDPrivilege = usePrivilege("MDM5");
  const divRef = useRef<HTMLDivElement | null>(null);
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [addClicked, setAddClicked] = useState(false);
  const [isConfirmationModal, setIsConfirmationModal] = useState<boolean>(false);
  const [submitBtnLoading, setSubmitBtnLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const handleSubmitRef = useRef<ServiceFeeAndRoleSelectionRef>(null);

  const [selectedReason, setSelectedReason] = useState<string>('');
  const [reasonInput, setReasonInput] = useState<string>('');
  const [reasonError, setReasonError] = useState<string>('');
  const [reasonInputError, setReasonInputError] = useState<string>('');

  useEffect(() => {
    dispatch(setUserUrl(window.location.pathname));

    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsRef.current &&
        !detailsRef.current.contains(event.target as Node)
      ) {
        detailsRef.current.open = false;
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const [masterDistributorDetails, setMasterDistributor] = useState<MasterDistributor>({
    id: '',
    user_code: '',
    user_role: '',
    customer_type: '',
    basic_details: {
      first_name: '',
      last_name: null,
      date_of_birth: null,
      gender: '',
      nationality: '',
      profession: '',
      country_code: '',
      phone_number: '',
      email_address: '',
      secondary_phone_numbers: null,
      country: '',
      city_town: null,
      house_no: null,
      market_block_no: null,
      floor_no: null,
      street_name: null,
      province: null,
      region: null,
      district: null,
      postal_code: '',
      location_name: null,
      latitude: null,
      longitude: null,
      recovery_email_address: '',
      business_assigned_area: {
        region: '',
        provinces: [],
      },
      wallet_access: null,
      onboarded_by: null,
    },
    status: null,
    encrypted_pin: null,
    photo: null,
    user_activity: null,
    profile: null,
    last_logged_in: null,
    identification_details: {
      identification_type: '',
      document_details: null,
    },
    bank_details: null,
    corporate_details: {
      company_id: "",
      company_name: "",
      business_type: "",
      contact_person_name: "",
      company_license_back: "",
      company_license_front: "",
      company_license_number: "",
      employee_id_card_number: "",
      representative_profession: "",
      company_license_expiry_date: "",
      employee_id_card_front_page: ""
    },
    business_details: null,
    service_fee_type: null,
    upfront_service_fee_percentage: null,
    extras: null,
    created_at: '',
    updated_at: '',
  });

  const basicDetailsHeadings = [
    "User Role",
    "Email Address",
    "Phone Number",
    "Date of Birth",
    "Gender",
    "Nationality",
    "Profession",
    "Wallet Type",
    "Registered By",
    ...((type === "Customers" && masterDistributorDetails?.customer_type === 'Corporate') ? ["Recovery Email Address"] : []),
    "Secondary Phone Numbers",
  ];
  const handleApproveRequest = async () => {
    try {
      const userRoleMap: { [key: string]: string } = {
        'Master Distributor': 'MDST',
        'Distributor': 'DST',
        'Sub Distributor': 'SDST',
        'Agent': 'AGT',
        'Retailer': 'RET',
        'Customer' : 'CMR',
        'Temporary User': 'TMP',
      };

      const userType = userRoleMap[masterDistributorDetails?.user_role || ''] || 'TMP';
      setSubmitBtnLoading(true)
      const headers = { 'x-source-page': tab === 'Onboarding' ? 'onboarding-request' : 'modification-request' };

      const response = await PatchAPI(
        `onboarding-requests/review/${id}`,
        {
          is_approved: true,
          user_type: userType
        }, true, headers

      );

      if (!response?.error) {
        dispatch(showToast({ message: "Request approved successfully", type: "success" }));
        navigate('/onboarding-request/onboarding')
        setAddClicked(false);
      } else {
        const errorMsg = response?.data?.response?.data?.message || 'Something went wrong';
        dispatch(showToast({ message: errorMsg, type: "error" }));
      }
    } catch (error) {
      console.error('API Error:', error);
      dispatch(showToast({ message: "An error occurred while approving the request", type: "error" }));
    } finally {
      // setButtonLoading(false);
      setSubmitBtnLoading(false);
    }
  };

  const fetchmasterDistributorDetails = async () => {
    setLoading(true);
    try {
      const headers = { 'x-source-page': 'modification-request' };
      const response = await getAPI(`${type.toLowerCase().replace(/ /g, '-')}/${id}`, true, tab === 'Modification' ? headers : null);
      if (!response?.error) {
        const temp = response?.data?.data;
        delete temp["created_at"];
        delete temp["updated_at"];
        setMasterDistributor(temp);
        dispatch(setCompleteServiceFee(temp?.service_fee))
        dispatch(setProvinces(temp?.basic_details?.business_assigned_area?.provinces || []));
        dispatch(setRegion(temp?.basic_details?.business_assigned_area?.region || ''))
      } else {
        navigate("/onboarding-request/onboarding");
      }
    } catch (error) {
      console.error("Error fetching admin details:", error);
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    fetchmasterDistributorDetails();
  }, []);



  const name = masterDistributorDetails.basic_details.first_name + ' ' + masterDistributorDetails.basic_details.last_name;
  const addressParts = [
    masterDistributorDetails?.basic_details?.house_no,
    masterDistributorDetails?.basic_details?.street_name,
    masterDistributorDetails?.basic_details?.market_block_no,
    masterDistributorDetails?.basic_details?.floor_no,
    masterDistributorDetails?.basic_details?.district,
    masterDistributorDetails?.basic_details?.city_town,
    masterDistributorDetails?.basic_details?.region,
    masterDistributorDetails?.basic_details?.country,
    masterDistributorDetails?.basic_details?.postal_code
  ];

  const addressString = addressParts
    .filter(part => part) // Remove null, undefined, and empty values
    .join(", "); // Join with comma only for valid values
  const getProfileBgColor = (status: any) => {
    if (status === "Approved") {
      return "bg-[#E8F7ED]";
    } else if (status === "In Review") {
      return "bg-[#E5F0FC]";
    } else if (status === "Pending" || status === "In Progress") {
      return "bg-[#FFEFD9]";
    } else if (status === "Rejected") {
      return "bg-[#FFE5E5]";
    } else {
      return "";
    }
  }
  /**
   * The function `getProfileTextColor` returns a specific text color based on the input status value
   * in a TypeScript React application.
   * @param {any} status - The `getProfileTextColor` function takes a `status` parameter as input and
   * returns a corresponding text color based on the status value. The possible status values and their
   * corresponding text colors are as follows:
   * @returns The function `getProfileTextColor` returns a string representing the color code based on
   * the input `status`. The color code returned depends on the value of `status`:
   */
  const getProfileTextColor = (status: any) => {
    if (status === "Approved") {
      return "text-[#22B14C]";
    } else if (status === "In Review") {
      return "text-[#0072E4]";
    } else if (status === "Pending" || status === "In Progress") {
      return "text-[#FD9101]";
    } else if (status === "Rejected") {
      return "text-[#FF0000]";
    } else {
      return "";
    }
  }

  //function to check the type of the submitted file
  const isImageUrl = (url: string): boolean => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  const isServiceFeeEmpty = !masterDistributorDetails?.service_fee || Object.keys(masterDistributorDetails.service_fee || {}).length === 0
  const getModalText = () => {
    const isBusinessAreaEmpty = !masterDistributorDetails?.basic_details?.business_assigned_area || Object.keys(masterDistributorDetails.basic_details.business_assigned_area || {}).length === 0

    if(type === 'Customers'){
      return "Are you sure you want to Approve this request?"
    }
    if (isServiceFeeEmpty) {
      return "Please provide the service fee details"
    } else {
      return "Please verify the service fee details"
    }
  }

  const handleReasonChange = (id: string, value: string) => {
    setReasonError('');
    setSelectedReason(value);
  }

  const handleRejectRequest = async () => {
    // Reset errors
    setReasonError('');
    setReasonInputError('');

    // Validate reason selection
    if (!selectedReason) {
      setReasonError('This field is required');
      return;
    }

    // Validate reason input if "Other" is selected
    if (selectedReason === 'Other' && !reasonInput.trim()) {
      setReasonInputError('This field is required');
      return;
    }

    try {
      setSubmitBtnLoading(true);
      const userRoleMap: { [key: string]: string } = {
        'Master Distributor': 'MDST',
        'Distributor': 'DST',
        'Sub Distributor': 'SDST',
        'Agent': 'AGT',
        'Retailer': 'RET',
        'Customer' : 'CMR',
        'Temporary User': 'TMP',
      };

      const userType = userRoleMap[masterDistributorDetails?.user_role || ''] || 'TMP';
      const headers = { 'x-source-page': tab === 'Onboarding' ? 'onboarding-request' : 'modification-request' };
      const response = await PatchAPI(
        `onboarding-requests/review/${id}`,
        {
          is_approved: false,
          user_type: userType,
          reason: selectedReason === 'Other' ? reasonInput : selectedReason
        },
        true, headers
      );

      if (!response?.error) {
        dispatch(showToast({ message: "Request rejected successfully", type: "success" }));
        navigate('/onboarding-request/onboarding');
        setIsRejectModalOpen(false);
      } else {
        const errorMsg = response?.data?.response?.data?.message || 'Something went wrong';
        dispatch(showToast({ message: errorMsg, type: "error" }));
      }
    } catch (error) {
      console.error('API Error:', error);
      dispatch(showToast({ message: "An error occurred while rejecting the request", type: "error" }));
    } finally {
      setSubmitBtnLoading(false);
    }
  };

  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <Breadcrumbs
        navPath={[
          "Onboarding / Modification Request",
          `List of ${tab} Requests`,
          `View ${tab} Details`,
          ...(addClicked ? ["Add Service Fee"] : []),
        ]}
        links={
          pathname
            ? [pathname, pathname]
            : [
              `/onboarding-request/onboarding`,
              `/onboarding-request/onboarding`,
            ]
        }
        heading={`View ${tab} Request`}
        setAddClicked={setAddClicked}
      />

      {loading ? <Shimmer /> : !loading && !addClicked ? (
        <div
          className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col"
          ref={divRef}
        >
          <div className="px-6 py-5 border-b border-[#E5E9EB] flex justify-between items-center">
            <div>
              <div
                title={name}
                data-testid="name"
                className="flex flex-wrap items-start gap-4 text-heading2"
              >
                <div
                  data-testid="user-name"
                  className="break-all max-w-[500px]"
                >
                  {name || "-"}
                </div>
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-lg shrink-0 ${getProfileBgColor(
                    masterDistributorDetails?.profile
                  )}`}
                >
                  <h1
                    data-testid="user-status"
                    className={`text-sm font-medium ${getProfileTextColor(
                      masterDistributorDetails?.profile
                    )}`}
                  >
                    {masterDistributorDetails?.profile}
                  </h1>
                </div>
              </div>
              <p
                title={masterDistributorDetails?.user_code}
                data-testid="user-code"
                className="text-subTitle"
              >
                {masterDistributorDetails?.user_code || "-"}
              </p>
            </div>
            {masterDistributorDetails?.profile === "In Review" && (
              <div className="flex gap-[24px] print:hidden">
                <Button
                  buttonName={translate("Approve")}
                  buttonOnclick={() => setIsModalOpen(true)}
                  className={"bg-positive w-[90px]"}
                  buttonHight="h-[40px]"
                  bgColor={"bg-positive"}
                  textSize="text-large"
                  testId="status-btn"
                />
                <Button
                  buttonName={translate("Reject")}
                  className="py-2 h-fit px-4 w-[90px]"
                  buttonHight="h-[40px]"
                  textSize="text-large"
                  buttonOnclick={() => {
                    setIsRejectModalOpen(true);
                  }}
                  testId="update-btn"
                  buttonDisabled={false}
                  bgColor={"bg-negative"}
                />
                <Image
                  testId={`edit-btn`}
                  src={`editImg`}
                  key={"edit"}
                  className="z-50 w-[24px] mt-[6px] h-[24px] cursor-pointer"
                  onClick={() => {
                    navigate(
                      `/${tab?.toLowerCase()}-request/${tab?.toLowerCase()}/edit-${tab?.toLowerCase()}-request/${type
                        .toLowerCase()
                        .replace(/ /g, "-")
                        .slice(0, -1)}/${id}`
                    );
                  }}
                />
              </div>
            )}
          </div>
          <div
            className={`flex-grow px-6 pb-6 my-5 ${getFirefoxCssFix(
              "scroll"
            )} overflow-auto`}
          >
            <div className="border-b-2 border-gray-200 pb-6">
              <div className="flex gap-3">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Basic Details
                </h1>
                {masterDistributorDetails?.modified_sections?.includes(
                  "basic_details"
                ) && (
                    <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                      Edited
                    </div>
                  )}
              </div>

              <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {basicDetailsHeadings.map((heading, index) => (
                  <div key={index} className="mb-4">
                    <h1 className="text-sm text-gray-600">{heading}</h1>
                    <p
                      data-testid={`${heading.replace(" ", "-").toLowerCase()}`}
                      className="mt-1 font-medium text-gray-900 break-all"
                    >
                      {(() => {
                        switch (heading) {
                          case "User Role":
                            return type === "Customers" ? `${masterDistributorDetails?.user_role} (${masterDistributorDetails?.customer_type})` : masterDistributorDetails?.user_role || '-';
                          case "Email Address":
                            return (
                              masterDistributorDetails?.basic_details
                                .email_address || "-"
                            );
                          case "Phone Number":
                            return (
                              (masterDistributorDetails?.basic_details
                                ?.country_code || "") +
                              " " +
                              (masterDistributorDetails?.basic_details
                                .phone_number || "") || "-"
                            );
                          case "Date of Birth":
                            return (
                              formatDate(
                                masterDistributorDetails?.basic_details
                                  .date_of_birth
                                  ? masterDistributorDetails?.basic_details
                                    .date_of_birth
                                  : ""
                              ) || "-"
                            );
                          case "Gender":
                            return (
                              masterDistributorDetails?.basic_details.gender ||
                              "-"
                            );
                          case "Nationality":
                            return (
                              masterDistributorDetails?.basic_details
                                .nationality || "-"
                            );
                          case "Profession":
                            return (
                              masterDistributorDetails?.basic_details
                                .profession || "-"
                            );
                          case "Wallet Type":
                            return (
                              masterDistributorDetails?.basic_details.wallet_access
                                ?.map((wallet) => wallet?.replace(/_/g, " "))
                                .join(" | ") || "-"
                            );
                          case "Registered By":
                            return (
                              masterDistributorDetails?.basic_details
                                .onboarded_by || "-"
                            );
                          case "Recovery Email Address":
                            return masterDistributorDetails?.basic_details?.recovery_email_address || "-";
                          case "Secondary Phone Numbers":
                            return masterDistributorDetails?.basic_details
                              .secondary_phone_numbers?.length
                              ? masterDistributorDetails.basic_details.secondary_phone_numbers.map(
                                ({ country_code, phone_number }, index) => (
                                  <div
                                    key={`${country_code}-${phone_number}`}
                                    data-testid={`phone-number-${index}`}
                                  >
                                    {country_code} {phone_number}
                                  </div>
                                )
                              )
                              : "-";
                          default:
                            return "-";
                        }
                      })()}
                    </p>
                  </div>
                ))}
              </div>
              <div>
                <div>
                  <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">
                    Residential Details
                  </h1>
                  <div className="mt-6">
                    <h1 className="text-sm text-gray-600">Address</h1>
                    <p
                      className="mt-1 font-medium text-gray-900 w-[30%]"
                      data-testid="address"
                    >
                      {addressString}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">
                    Business Assigned Area
                  </h1>

                  <div>
                    {masterDistributorDetails?.basic_details
                      ?.business_assigned_area ? (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <h2 className="text-sm text-gray-600">Region</h2>
                          <p
                            className="mt-1 font-medium text-gray-900"
                            data-testid="region"
                          >
                            {masterDistributorDetails?.basic_details
                              ?.business_assigned_area?.region || "-"}
                          </p>
                        </div>
                        <div>
                          <h2 className="text-sm text-gray-600">
                            Province (State)
                          </h2>
                          <p
                            className="mt-1 font-medium text-gray-900"
                            data-testid="province"
                          >
                            {masterDistributorDetails?.basic_details?.business_assigned_area?.provinces?.join(
                              ", "
                            ) || "-"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1 font-medium text-gray-900">-</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 border-b-2 border-gray-200 pb-6">
              <div className="flex gap-3">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Identification Details
                </h1>
                {masterDistributorDetails?.modified_sections?.includes(
                  "identification_details"
                ) && (
                    <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                      Edited
                    </div>
                  )}
              </div>
              {masterDistributorDetails?.identification_details ? (
                <div>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <h1 className="text-sm text-gray-600">Choosen ID</h1>
                      <p className="mt-1 font-medium text-gray-900 break-all">
                        {masterDistributorDetails.identification_details
                          ?.identification_type || "-"}
                      </p>
                    </div>
                    {masterDistributorDetails.identification_details
                      ?.identification_type === "Passport" &&
                      renderFields(
                        masterDistributorDetails.identification_details
                          .document_details as Passport,
                        passportFields
                      )}
                    {masterDistributorDetails.identification_details
                      ?.identification_type === "National ID" &&
                      renderFields(
                        masterDistributorDetails.identification_details
                          .document_details as NationalID,
                        nationalIdFields
                      )}
                  </div>
                </div>
              ) : (
                <p className="mt-1 font-medium text-gray-900">-</p>
              )}
            </div>
            <div className="mt-6 border-b-2 border-gray-200 pb-4">
              <div className="flex gap-3">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Bank Details
                </h1>
                {masterDistributorDetails?.modified_sections?.includes(
                  "bank_details"
                ) && (
                    <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                      Edited
                    </div>
                  )}
              </div>
              <div>
                {masterDistributorDetails?.bank_details &&
                  masterDistributorDetails?.bank_details.length > 0 ? (
                  masterDistributorDetails?.bank_details?.map((bank, index) => (
                    <div className="mt-6 flex flex-col gap-5">
                      <div>
                        <div
                          key={index}
                          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                        >
                          <div>
                            <h1 className="text-sm text-gray-600">Bank Name</h1>
                            <p className="mt-1 font-medium text-gray-900 break-all">
                              {bank.bank_name || "-"}
                            </p>
                          </div>
                          <div>
                            <h1 className="text-sm text-gray-600">
                              Account Number
                            </h1>
                            <p className="mt-1 font-medium text-gray-900 break-all">
                              {bank.account_number || "-"}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 mb-4 w-1/3">
                          <h1 className="text-sm text-gray-600">
                            Bank Passbook Front Page
                          </h1>
                          {bank?.bank_passbook_front_page ? (
                            <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 ">
                              <p className="more-lines">
                                {bank?.bank_passbook_front_page
                                  .split("/")
                                  .pop()}
                              </p>
                              <Image
                                src="file-eye"
                                className="cursor-pointer "
                                objectFit={"contain"}
                                onClick={() => {
                                  const fileUrl =
                                    config?.cdnURL +
                                    "public/" +
                                    bank?.bank_passbook_front_page;

                                  const link = document.createElement("a");
                                  link.href = fileUrl;
                                  link.target = "_blank";
                                  link.rel = "noopener noreferrer";
                                  link.click();
                                }}
                              />
                            </div>
                          ) : (
                            <p className="mt-1 font-medium text-gray-900">-</p>
                          )}
                          <div className="hidden mt-2 print:block ">
                            {isImageUrl(
                              config?.cdnURL +
                              "public/" +
                              bank?.bank_passbook_front_page
                            ) && (
                                <img
                                  src={
                                    config?.cdnURL +
                                    "public/" +
                                    bank?.bank_passbook_front_page
                                  }
                                  alt={
                                    config?.cdnURL +
                                    "public/" +
                                    bank?.bank_passbook_front_page
                                  }
                                  className="h-[200px] w-[200px] object-contain"
                                />
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="mt-3 font-medium text-gray-900">-</p>
                )}
              </div>
            </div>
            {type !== 'Customers' && <div className="mt-6 border-b-2 border-gray-200 pb-4">
              <div className="flex gap-3">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Business Details
                </h1>
                {masterDistributorDetails?.modified_sections?.includes(
                  "business_details"
                ) && (
                    <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                      Edited
                    </div>
                  )}
              </div>
              {masterDistributorDetails?.business_details ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ">
                    <div className="mt-6">
                      <h1 className="text-sm text-gray-600">Store Name</h1>
                      <p className="mt-1 font-medium text-gray-900 break-all">
                        {masterDistributorDetails?.business_details
                          ?.store_name || "-"}
                      </p>
                    </div>
                    <div className="mt-6">
                      <h1 className="text-sm text-gray-600">Business Type</h1>
                      <p className="mt-1 font-medium text-gray-900 break-all">
                        {masterDistributorDetails?.business_details
                          ?.business_type || "-"}
                      </p>
                    </div>
                    <div className="mt-6">
                      <h1 className="text-sm text-gray-600">
                        Business License Number
                      </h1>
                      <p className="mt-1 font-medium text-gray-900 break-all">
                        {masterDistributorDetails?.business_details
                          ?.business_license_number || "-"}
                      </p>
                    </div>
                    <div className="mt-6">
                      <h1 className="text-sm text-gray-600">
                        Business License Valid From
                      </h1>
                      <p className="mt-1 font-medium text-gray-900">
                        {formatDate(
                          masterDistributorDetails?.business_details
                            ?.business_valid_from
                            ? masterDistributorDetails?.business_details
                              ?.business_valid_from
                            : ""
                        ) || "-"}
                      </p>
                    </div>
                    <div className="mt-6">
                      <h1 className="text-sm text-gray-600">
                        Business License Valid Up To
                      </h1>
                      <p className="mt-1 font-medium text-gray-900">
                        {formatDate(
                          masterDistributorDetails?.business_details
                            ?.business_valid_to
                            ? masterDistributorDetails?.business_details
                              ?.business_valid_to
                            : ""
                        ) || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="mt-4 mb-4">
                      <h1 className="text-sm text-gray-600">
                        Business License Front Page
                      </h1>
                      {masterDistributorDetails?.business_details
                        ?.business_license_front_page ? (
                        <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                          <p className="more-lines">
                            {masterDistributorDetails?.business_details?.business_license_front_page
                              ?.split("/")
                              .pop()}
                          </p>
                          <Image
                            src="file-eye"
                            className="cursor-pointer"
                            objectFit={"contain"}
                            onClick={() => {
                              const fileUrl =
                                config?.cdnURL +
                                "public/" +
                                masterDistributorDetails?.business_details
                                  ?.business_license_front_page;
                              window.open(fileUrl, "_blank");
                            }}
                          />
                        </div>
                      ) : (
                        <p className="mt-1 font-medium text-gray-900">-</p>
                      )}
                      <div className="hidden mt-2 print:block ">
                        {isImageUrl(
                          config?.cdnURL +
                          "public/" +
                          masterDistributorDetails?.business_details
                            ?.business_license_front_page
                        ) && (
                            <img
                              src={
                                config?.cdnURL +
                                "public/" +
                                masterDistributorDetails?.business_details
                                  ?.business_license_front_page
                              }
                              alt={
                                config?.cdnURL +
                                "public/" +
                                masterDistributorDetails?.business_details
                                  ?.business_license_front_page
                              }
                              className="h-[200px] w-[200px] object-contain"
                            />
                          )}
                      </div>
                    </div>
                    <div className="mt-4 mb-4">
                      <h1 className="text-sm text-gray-600">
                        Business License Back Page
                      </h1>
                      {masterDistributorDetails?.business_details
                        ?.business_license_back_page ? (
                        <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                          <p className="more-lines">
                            {masterDistributorDetails?.business_details?.business_license_back_page
                              ?.split("/")
                              .pop()}
                          </p>
                          <Image
                            src="file-eye"
                            className="cursor-pointer"
                            objectFit={"contain"}
                            onClick={() => {
                              const fileUrl =
                                config?.cdnURL +
                                "public/" +
                                masterDistributorDetails?.business_details
                                  ?.business_license_back_page;
                              window.open(fileUrl, "_blank");
                            }}
                          />
                        </div>
                      ) : (
                        <p className="mt-1 font-medium text-gray-900">-</p>
                      )}
                      <div className="hidden mt-2 print:block ">
                        {isImageUrl(
                          config?.cdnURL +
                          "public/" +
                          masterDistributorDetails?.business_details
                            ?.business_license_back_page
                        ) && (
                            <img
                              src={
                                config?.cdnURL +
                                "public/" +
                                masterDistributorDetails?.business_details
                                  ?.business_license_back_page
                              }
                              alt={
                                config?.cdnURL +
                                "public/" +
                                masterDistributorDetails?.business_details
                                  ?.business_license_back_page
                              }
                              className="h-[200px] w-[200px] object-contain"
                            />
                          )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className=" mt-6">-</p>
              )}
            </div>}
            <div className={`mt-6 ${(type === 'Customers' && masterDistributorDetails?.customer_type === 'Individual') ? '' : 'border-b-2 pb-4'} border-gray-200 `}>
              <div className="flex gap-3">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Photo
                </h1>
                {masterDistributorDetails?.modified_sections?.includes(
                  "photo"
                ) && (
                    <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                      Edited
                    </div>
                  )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ">
                <div className="mt-6">
                  <div className=" mb-4">
                    {masterDistributorDetails?.photo ? (
                      <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                        <p className="more-lines">
                          {masterDistributorDetails?.photo?.split("/").pop()}
                        </p>
                        <Image
                          src="file-eye"
                          className="cursor-pointer"
                          objectFit={"contain"}
                          onClick={() => {
                            const fileUrl =
                              config?.cdnURL +
                              "public/" +
                              masterDistributorDetails?.photo;
                            window.open(fileUrl, "_blank");
                          }}
                        />
                      </div>
                    ) : (
                      <p className="mt-1 font-medium text-gray-900">-</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Service Fee Section */}
            {type !== 'Customers' && <div className="mt-6 border-b-2 border-gray-200 pb-4">
              <div className="flex gap-3">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Service Fee
                </h1>
                {masterDistributorDetails?.modified_sections?.includes(
                  "service_fee"
                ) && (
                    <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                      Edited
                    </div>
                  )}
              </div>
              {masterDistributorDetails?.service_fee ? (
                <div>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <h1 className="text-sm text-gray-600">Parent User</h1>
                      <p className="mt-1 font-medium text-gray-900 break-all">
                        {masterDistributorDetails?.service_fee
                          ?.service_fee_parent || "-"}
                      </p>
                    </div>
                    <div>
                      <h1 className="text-sm text-gray-600">
                        Service Fee Type
                      </h1>
                      <p className="mt-1 font-medium text-gray-900 break-all">
                        {masterDistributorDetails?.service_fee
                          ?.service_fee_type || "-"}
                      </p>
                    </div>
                    {masterDistributorDetails?.service_fee?.service_fee_type ===
                      "Upfront" && (
                        <div>
                          <h1 className="text-sm text-gray-600">
                            Service Fee Percentage
                          </h1>
                          <p className="mt-1 font-medium text-gray-900 break-all">
                            {masterDistributorDetails?.service_fee
                              ?.service_fee_type === "Upfront"
                              ? masterDistributorDetails?.service_fee
                                ?.upfront_service_fee_percentage !== null &&
                                masterDistributorDetails?.service_fee
                                  ?.upfront_service_fee_percentage !== undefined
                                ? `${masterDistributorDetails?.service_fee?.upfront_service_fee_percentage}%`
                                : "-"
                              : "-"}
                          </p>
                        </div>
                      )}
                  </div>
                  {masterDistributorDetails?.service_fee?.service_fee_type ===
                    "Postpaid" &&
                    masterDistributorDetails?.service_fee
                      ?.service_fee_details && (
                      <div className="mt-5">
                        <div className="w-full bg-[#F4F8FF] rounded-lg p-4">
                          <div className="flex text-gray-700 text-sm mb-2">
                            <div className="w-3/12">Telecom</div>
                            <div className="w-3/12">
                              eTop Up Service Fee (%)
                            </div>
                            <div className="w-3/12">Bundle Service Fee (%)</div>
                            <div className="w-2/12">Updated Date</div>
                            <div className="w-1/12">Status</div>
                          </div>
                          {masterDistributorDetails.service_fee.service_fee_details.map(
                            (detail, index) => (
                              <div
                                key={index}
                                className="flex text-gray-900 font-medium text-sm py-1 items-center"
                              >
                                <div className="w-3/12">
                                  {detail.mno_name || "-"}
                                </div>
                                <div className="w-3/12">
                                  {detail.etopup_dispersive_fee_percent !==
                                    undefined &&
                                    detail.etopup_dispersive_fee_percent !== null
                                    ? detail.etopup_dispersive_fee_percent
                                    : "-"}
                                </div>
                                <div className="w-3/12">
                                  {detail.bundle_dispersive_fee_percent !==
                                    undefined &&
                                    detail.bundle_dispersive_fee_percent !== null
                                    ? detail.bundle_dispersive_fee_percent
                                    : "-"}
                                </div>
                                <div className="w-2/12">-</div>
                                <div className="w-1/12">
                                  {detail.status ? "Active" : "Inactive"}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="mt-3 font-medium text-gray-900">-</p>
              )}
            </div>}
            {/* CVM Section */}
            {type !== 'Customers' && <div className="mt-6">
              <div className="flex items-center justify-between">
                <div className="flex gap-3">
                  <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                    CVM
                  </h1>
                  {masterDistributorDetails?.modified_sections?.includes(
                    "cvm"
                  ) && (
                      <div className="bg-gray-200 text-sm w-fit h-fit py-[2px] px-4 rounded-md">
                        Edited
                      </div>
                    )}
                </div>
              </div>
              {masterDistributorDetails?.cvm ? (
                <div className="mt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div>
                      <h1 className="text-sm text-gray-600">From Date</h1>
                      <p className="mt-1 font-medium text-gray-900">
                        {formatDate(
                          masterDistributorDetails?.cvm?.cvm_from_date
                        ) || "-"}
                      </p>
                    </div>
                    <div>
                      <h1 className="text-sm text-gray-600">To Date</h1>
                      <p className="mt-1 font-medium text-gray-900">
                        {formatDate(
                          masterDistributorDetails?.cvm?.cvm_to_date
                        ) || "-"}
                      </p>
                    </div>
                  </div>
                  {masterDistributorDetails?.cvm?.targets &&
                    masterDistributorDetails?.cvm?.targets.length > 0 && (
                      <div className="grid grid-row-1 sm:grid-row-2 lg:grid-row-3 gap-4">
                        {masterDistributorDetails?.cvm?.targets.map(
                          (target, index) => (
                            <div
                              key={index}
                              className="mb-4 flex w-full items-center"
                            >
                              <div className="w-1/3">
                                <h1 className="text-sm text-gray-600">
                                  Target
                                </h1>
                                <p className="mt-1 font-medium text-gray-900">
                                  {target.target_amount || "-"}
                                </p>
                              </div>
                              <div className="w-1/3">
                                <h1 className="text-sm text-gray-600">
                                  On Target Achievement Percentage
                                </h1>
                                <p className="mt-1 font-medium text-gray-900">
                                  {target.on_target_achievement_percentage !==
                                    undefined &&
                                    target.on_target_achievement_percentage !==
                                    null
                                    ? `${target.on_target_achievement_percentage}%`
                                    : "-"}
                                </p>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <p className="mt-3 font-medium text-gray-900">-</p>
              )}
            </div>}
            {/* Corporate Details Section */}
            {type === 'Customers' && masterDistributorDetails?.customer_type === 'Corporate' && (
              <div className="mt-6">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400" data-testid="corporate-details-heading">Corporate Details</h1>
                {masterDistributorDetails?.corporate_details ? (
                  <div>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="company-name-label">Company Name</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="company-name">{masterDistributorDetails?.corporate_details?.company_name || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="contact-person-name-label">Point of Contact Person Name</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="contact-person-name">{masterDistributorDetails?.corporate_details?.contact_person_name || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="contact-email-label">Point of Contact Person's Email Address</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="contact-email">{masterDistributorDetails?.corporate_details?.contact_email || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="representative-profession-label">Point of Contact Person's Profession</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="representative-profession">{masterDistributorDetails?.corporate_details?.representative_profession || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="company-license-expiry-date-label">Company License Expiry Date</h1>
                        <p className="mt-1 font-medium text-gray-900" data-testid="company-license-expiry-date">{formatDate(masterDistributorDetails?.corporate_details?.company_license_expiry_date) || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="business-type-label">Business Type</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="business-type">{masterDistributorDetails?.corporate_details?.business_type || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="company-license-number-label">Company License Number</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="company-license-number">{masterDistributorDetails?.corporate_details?.company_license_number || '-'}</p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600" data-testid="employee-id-number-label">Employee ID Card Number</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all" data-testid="employee-id-card-number">{masterDistributorDetails?.corporate_details?.employee_id_card_number || '-'}</p>
                      </div>

                    </div>

                    <div className="flex w-full mt-6">
                      <div className="w-1/3">
                        <h1 className="text-sm text-gray-600" data-testid="license-front-label">Company License Front Page</h1>
                        {masterDistributorDetails?.corporate_details?.company_license_front ? (
                          <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1" data-testid="license-front-container">
                            <p className="more-lines" data-testid="license-front-filename">{masterDistributorDetails?.corporate_details?.company_license_front?.split("/").pop()}</p>
                            <Image
                              src="file-eye"
                              className="cursor-pointer"
                              objectFit="contain"
                              testId="company-license-front-page-image"
                              onClick={() => window.open(config?.cdnURL + 'public/' + masterDistributorDetails?.corporate_details?.company_license_front, "_blank")}
                            />
                          </div>
                        ) : (
                          <p className="mt-1 font-medium text-gray-900" data-testid="license-front-empty">-</p>
                        )}
                      </div>
                      <div className="w-1/3">
                        <h1 className="text-sm text-gray-600" data-testid="license-back-label">Company License Back Page</h1>
                        {masterDistributorDetails?.corporate_details?.company_license_back ? (
                          <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1" data-testid="license-back-container">
                            <p className="more-lines" data-testid="license-back-filename">{masterDistributorDetails?.corporate_details?.company_license_back?.split("/").pop()}</p>
                            <Image
                              src="file-eye"
                              className="cursor-pointer"
                              objectFit="contain"
                              testId="company-license-back-page-image"
                              onClick={() => window.open(config?.cdnURL + 'public/' + masterDistributorDetails?.corporate_details?.company_license_back, "_blank")}
                            />
                          </div>
                        ) : (
                          <p className="mt-1 font-medium text-gray-900" data-testid="license-back-empty">-</p>
                        )}
                      </div>
                    </div>
                    <div className="w-1/3 mt-6">
                      <h1 className="text-sm text-gray-600" data-testid="employee-id-front-label">Employee ID Card Front Page</h1>
                      {masterDistributorDetails?.corporate_details?.employee_id_card_front_page ? (
                        <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1" data-testid="employee-id-front-container">
                          <p className="more-lines" data-testid="employee-id-front-filename">{masterDistributorDetails?.corporate_details?.employee_id_card_front_page?.split("/").pop()}</p>
                          <Image
                            src="file-eye"
                            className="cursor-pointer"
                            objectFit="contain"
                            testId="employee-id-card-front-page-image"
                            onClick={() => window.open(config?.cdnURL + 'public/' + masterDistributorDetails?.corporate_details?.employee_id_card_front_page, "_blank")}
                          />
                        </div>
                      ) : (
                        <p className="mt-1 font-medium text-gray-900" data-testid="employee-id-front-empty">-</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 font-medium text-gray-900" data-testid="corporate-details-empty">-</p>
                )}
              </div>
            )}
          </div>
        </div>
      ) : !loading && addClicked && type !== 'Customers' && (
        <>
          <AddModel>
            <div className="w-full">
              <ServiceFeeAndRoleSelection
                isTemporaryUser={
                  masterDistributorDetails?.user_role === "Temporary User"
                }
                setEditLoading={setAddClicked}
                type={masterDistributorDetails?.user_role}
                ref={handleSubmitRef}
                user_id={id || ""}
                userRole={masterDistributorDetails?.user_role}
                userType={masterDistributorDetails?.user_type}
                businessAssignedArea={
                  Object(
                    masterDistributorDetails?.basic_details
                      ?.business_assigned_area
                  ).length > 0
                    ? masterDistributorDetails?.basic_details
                      ?.business_assigned_area
                    : undefined
                }
                setIsConfirmationModal={setIsConfirmationModal}
                isConfirmationModal={isConfirmationModal}
                intendedUserRole={masterDistributorDetails?.intended_user_role}
                handleApproveRequest={handleApproveRequest}
                submitBtnLoading={submitBtnLoading}
                setButtonLoading={setButtonLoading}
              />
            </div>
          </AddModel>
          <AddBottomButton
            buttonName={"Submit"}
            onClick={() => {
              if (handleSubmitRef.current) {
                handleSubmitRef.current.handleSubmit();
              }
            }}
            loading={buttonLoading}
            buttonWidth="w-24"
          />
        </>
      )}
      {isModalOpen && type !== 'Customers' && (
        <CustomModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          title={type !== 'Customers' ? `${isServiceFeeEmpty ? "Add" : "Verify"} Service Fee Details` : "Approve the Request"}
          subText={getModalText()}
          onHandleClose={() => handleApproveRequest(masterDistributorDetails?.user_role)}
          showCloseButton={true}
          primaryButtonDisabled={false}
          secondaryButtonDisabled={type !== 'Customers' && isServiceFeeEmpty}
          secondaryButtonLoading={submitBtnLoading}
          secondaryWidth="w-28"
          primaryButtonText={type === 'Customers' ? "Approve" : isServiceFeeEmpty ? "Continue" : "Verify"}
          reverseButton={type !== 'Customers' ? "w-fit flex-row-reverse" : ""}
          secondaryButtonText={type !== 'Customers' ? "Approve" : ""}
          handelSubmit={() => {
            if (type !== 'Customers') {
              setAddClicked(true);
              setIsModalOpen(false);
            }
          }}
          maxWidth="max-w-[461px]"
          buttonWidth={type === 'Customers' ? "w-[100px]" : isServiceFeeEmpty ? "w-[100px]" : "w-[85px]"}
        />
      )} 
      {isModalOpen && type === 'Customers' && (
        <CustomModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          title="Approve the Request"
          subText="Are you sure you want to approve the request?"
          showCloseButton={true}
          primaryButtonDisabled={false}
          primaryButtonText={'Approve'}
          handelSubmit={() => {
            handleApproveRequest(masterDistributorDetails?.user_role)
          }}
          maxWidth="max-w-[461px]"
          buttonWidth={"w-[95px]"}
          secondaryButton={false}
          buttonLoading={submitBtnLoading}
        />
      )}      
      {isRejectModalOpen && (
        <CustomModal
          isOpen={isRejectModalOpen}
          onClose={() => {
            setIsRejectModalOpen(false);
            setReasonError("");
            setReasonInputError("");
            setSelectedReason("");
            setReasonInput("");
          }}
          title={"Reject Request"}
          closeOnOverlayClick={false}
          subText={"Are you sure you want to reject this onboarding request?"}
          primaryButtonText={"Submit"}
          secondaryButtonText="Cancel"
          handelSubmit={handleRejectRequest}
          children={
            <div className={`${selectedReason === "Other" ? 'mb-2' : 'mb-4'}`}>
              <DropdownButtonForSearch
                options={[
                  "National ID issue",
                  "Under 17 years old",
                  "Photo issue",
                  "Parent is not identified",
                  "Other",
                ]}
                labelName={"Reason"}
                id={"reason"}
                tabIndex={0}
                noSearch={true}
                className={"w-full"}
                onChange={handleReasonChange}
                selectedValue={selectedReason}
                required={true}
                errorMessage={reasonError}
                testId={"reason-dropdown"}
                dropdownTestId={""}
                placeholder={"reason"}
                notAbsolute={selectedReason === "Other" ? false : true}
              />
              {selectedReason === "Other" && (
                <TextArea
                  label="Description"
                  required={true}
                  placeholderRequied={false}
                  text={reasonInput}
                  setText={setReasonInput}
                  textError={reasonInputError}
                  setTextError={setReasonInputError}
                  bgColor={"bg-white"}
                  maxLength={250}
                  id={`reason-note`}
                  height={"h-[180px]"}
                />
              )}
            </div>
          }
          maxWidth="max-w-[461px]"
          buttonWidth={"w-[100px]"}
          buttonLoading={submitBtnLoading}
        />
      )}
    </div>
  );
}
